import { useMutation } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import {
  BulkDocumentExportRequestError,
  requestBulkDocumentExport,
} from './request-bulk-document-export';
import type { BulkDocumentType } from './program-export-availability';

/**
 * DEV-498 — "Currículos em massa" / "Laudos em massa": a solicitação responde
 * na hora com UM de quatro Sonners neutros, todos com "Fechar" (AC-05..AC-11):
 *
 *   202                              → "Exportação em massa iniciada"
 *                                      + "Você receberá um e-mail com o link da pasta."
 *   422 program_has_no_resumes       → "Programa ainda sem currículos"
 *   422 program_has_no_attestations  → "Programa ainda sem laudos"
 *   qualquer outra falha             → "Algo deu errado. Tente novamente"
 *
 * Nada baixa e a tela segue utilizável (AC-07): o trabalho é do servidor, que
 * continua mesmo com o toast fechado, a página recarregada ou abandonada
 * (AC-12). Rede degradada: ou o toast de início ou o de erro — a mutation tem
 * UM desfecho. Clique duplo: a request em voo do MESMO (programa, tipo) é
 * ignorada aqui (um toast), e o servidor deduplica de qualquer jeito (AC-13).
 *
 * Toasts neutros (`toast(...)`, não `toast.error`) — padrão da EU-654 (V53).
 */
export const BULK_EXPORT_STARTED_TITLE = 'Exportação em massa iniciada';
export const BULK_EXPORT_STARTED_DESCRIPTION = 'Você receberá um e-mail com o link da pasta.';
export const BULK_EXPORT_NO_RESUMES = 'Programa ainda sem currículos';
export const BULK_EXPORT_NO_ATTESTATIONS = 'Programa ainda sem laudos';
export const BULK_EXPORT_FAILED = 'Algo deu errado. Tente novamente';

const pendingRequests = new Set<string>();

function toastId(programId: string, type: BulkDocumentType): string {
  return `program-bulk-export-${programId}-${type}`;
}

function closable(id: string) {
  return { id, duration: Infinity, action: { label: 'Fechar', onClick: () => toast.dismiss(id) } };
}

/** Mensagem do toast para a falha da solicitação (exportado para teste). */
export function bulkExportErrorMessage(err: unknown): string {
  if (err instanceof BulkDocumentExportRequestError && err.status === 422) {
    if (err.code === 'program_has_no_resumes') return BULK_EXPORT_NO_RESUMES;
    if (err.code === 'program_has_no_attestations') return BULK_EXPORT_NO_ATTESTATIONS;
  }
  return BULK_EXPORT_FAILED;
}

export function useBulkDocumentExport() {
  const mutation = useMutation({
    mutationFn: async (input: { programId: string; type: BulkDocumentType }) =>
      requestBulkDocumentExport(input.programId, input.type),
    onSuccess: (_result, input) => {
      const id = toastId(input.programId, input.type);
      toast(BULK_EXPORT_STARTED_TITLE, {
        ...closable(id),
        description: BULK_EXPORT_STARTED_DESCRIPTION,
      });
    },
    onError: (err, input) => {
      const id = toastId(input.programId, input.type);
      toast(bulkExportErrorMessage(err), closable(id));
    },
    onSettled: (_data, _err, input) => {
      pendingRequests.delete(`${input.programId}:${input.type}`);
    },
  });

  return {
    ...mutation,
    mutate: (input: { programId: string; type: BulkDocumentType }) => {
      const key = `${input.programId}:${input.type}`;
      if (pendingRequests.has(key)) return;
      pendingRequests.add(key);
      mutation.mutate(input);
    },
  };
}
